// services/assessmentTemplateService.ts
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  writeBatch,
  arrayUnion,
  arrayRemove
} from "firebase/firestore";
import { db } from "../firebase/config";

export interface AssessmentTemplate {
  id: string;
  name: string;
  description: string;
  targetAudience: "student" | "employee" | "leader" | "all";
  sections: {
    sectionId: string;
    sectionName: string;
    traits: string[];
    questionSelectionRule: "random" | "fixed";
    numberOfQuestions: number;
    order: number;
  }[];
  totalQuestions: number;
  totalDuration: number;
  instructions: string;
  timeLimit: number;
  isActive: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface AssessmentTemplateForm {
  name: string;
  description: string;
  targetAudience: "student" | "employee" | "leader" | "all";
  sections: {
    sectionId: string;
    sectionName: string;
    traits: string[];
    questionSelectionRule: "random" | "fixed";
    numberOfQuestions: number;
    order: number;
  }[];
  instructions: string;
  timeLimit: number;
  isActive: boolean;
}

const TEMPLATES_COLLECTION = "assessmentTemplates";

// Get all assessment templates for a specific user only
export const getAssessmentTemplates = async (userId: string): Promise<AssessmentTemplate[]> => {
  try {
    if (!userId) {
      console.warn("No userId provided, returning empty array");
      return [];
    }

    const q = query(
      collection(db, TEMPLATES_COLLECTION),
      where("createdBy", "==", userId)
    );

    const querySnapshot = await getDocs(q);

    const templates = [];

    for (const docSnap of querySnapshot.docs) {
      const data = docSnap.data();

      let createdByName = "System";

      if (data.createdBy) {
        const userQuery = query(
          collection(db, "users"),
          where("authUid", "==", data.createdBy)
        );

        const userSnap = await getDocs(userQuery);

        if (!userSnap.empty) {
          const userData = userSnap.docs[0].data();
          createdByName = userData.name || "User";
        }
      }

      templates.push({
        id: docSnap.id,
        name: data.name || "",
        description: data.description || "",
        targetAudience: data.targetAudience || "all",
        sections: data.sections || [],
        totalQuestions: data.totalQuestions || 0,
        totalDuration: data.totalDuration || 0,
        instructions: data.instructions || "",
        timeLimit: data.timeLimit || 0,
        isActive: data.isActive || false,
        createdBy: data.createdBy || "",
        createdByName: createdByName,
        createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        updatedAt: data.updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      });
    }

    return templates.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

  } catch (error) {
    console.error("Error fetching assessment templates:", error);
    throw error;
  }
};

// Get assessment template by ID (with ownership validation)
export const getAssessmentTemplateById = async (
  id: string,
  userId: string
): Promise<AssessmentTemplate | null> => {
  try {
    if (!userId) {
      throw new Error("User ID is required to access template");
    }

    const docRef = doc(db, TEMPLATES_COLLECTION, id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();

      // Permission check
      if (data.createdBy !== userId) {
        throw new Error("You don't have permission to access this template");
      }

      let createdByName = "System";
      let organizationName = "System";

      // If organizationName already stored in template use it
      if (data.organizationName) {
        organizationName = data.organizationName;
        createdByName = data.organizationName;
      } else if (data.createdBy) {
        // Otherwise fetch user
        const userDoc = await getDoc(doc(db, "users", data.createdBy));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          createdByName = userData.name || "User";

          // Fetch organization
          if (userData.organizationId) {
            const orgDoc = await getDoc(doc(db, "organizations", userData.organizationId));
            if (orgDoc.exists()) {
              organizationName = orgDoc.data().name;
            }
          }
        }
      }

      return {
        id: docSnap.id,
        name: data.name || "",
        description: data.description || "",
        targetAudience: data.targetAudience || "all",
        sections: data.sections || [],
        totalQuestions: data.totalQuestions || 0,
        totalDuration: data.totalDuration || 0,
        instructions: data.instructions || "",
        timeLimit: data.timeLimit || 0,
        isActive: data.isActive || false,
        createdBy: data.createdBy || "",
        createdByName: createdByName,
        createdByOrgName: organizationName,
        createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        updatedAt: data.updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      } as AssessmentTemplate;
    }

    return null;
  } catch (error) {
    console.error("Error fetching assessment template:", error);
    throw error;
  }
};

// Create assessment template (automatically sets current user as creator)
export const createAssessmentTemplate = async (
  templateData: AssessmentTemplateForm,
  userId: string,
  organizationId: string,
  organizationName: string,
  role: string
): Promise<string> => {
  try {
    const now = Timestamp.now();

    const totalQuestions = templateData.sections.reduce((sum, s) => sum + s.numberOfQuestions, 0);
    const totalDuration = templateData.sections.reduce((sum, s) => {
      return sum + (s.numberOfQuestions * 1.5);
    }, 0);

    let orgName = "System";

    if (organizationId) {
      const orgDoc = await getDoc(doc(db, "organizations", organizationId));
      if (orgDoc.exists()) {
        orgName = orgDoc.data().name || "Organization";
      }
    }

    const docRef = await addDoc(collection(db, TEMPLATES_COLLECTION), {
      ...templateData,
      totalQuestions,
      totalDuration: templateData.timeLimit || totalDuration,
      createdBy: userId,
      organizationId: organizationId,
      organizationName: orgName,
      createdAt: now,
      updatedAt: now,
    });

    return docRef.id;
  } catch (error) {
    console.error("Error creating assessment template:", error);
    throw error;
  }
};

// Update assessment template (with ownership validation)
export const updateAssessmentTemplate = async (
  id: string,
  templateData: Partial<AssessmentTemplateForm>,
  userId: string
): Promise<void> => {
  try {
    if (!userId) {
      throw new Error("User ID is required to update template");
    }

    // First verify ownership
    const template = await getAssessmentTemplateById(id, userId);
    if (!template) {
      throw new Error("Template not found or you don't have permission to update it");
    }

    const docRef = doc(db, TEMPLATES_COLLECTION, id);
    const updateData: any = {
      ...templateData,
      updatedAt: Timestamp.now(),
    };

    // Recalculate totals if sections changed
    if (templateData.sections) {
      updateData.totalQuestions = templateData.sections.reduce((sum, s) => sum + s.numberOfQuestions, 0);
      updateData.totalDuration = templateData.timeLimit ||
        templateData.sections.reduce((sum, s) => sum + (s.numberOfQuestions * 1.5), 0);
    }

    await updateDoc(docRef, updateData);
  } catch (error) {
    console.error("Error updating assessment template:", error);
    throw error;
  }
};

// Delete assessment template (with ownership validation)
export const deleteAssessmentTemplate = async (id: string, userId: string): Promise<void> => {
  try {
    if (!userId) {
      throw new Error("User ID is required to delete template");
    }

    // First verify ownership
    const template = await getAssessmentTemplateById(id, userId);
    if (!template) {
      throw new Error("Template not found or you don't have permission to delete it");
    }

    const docRef = doc(db, TEMPLATES_COLLECTION, id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error("Error deleting assessment template:", error);
    throw error;
  }
};

// Toggle template active status (with ownership validation)
export const toggleTemplateStatus = async (id: string, isActive: boolean, userId: string): Promise<void> => {
  try {
    if (!userId) {
      throw new Error("User ID is required to toggle template status");
    }

    // First verify ownership
    const template = await getAssessmentTemplateById(id, userId);
    if (!template) {
      throw new Error("Template not found or you don't have permission to modify it");
    }

    const docRef = doc(db, TEMPLATES_COLLECTION, id);
    await updateDoc(docRef, {
      isActive,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error("Error toggling template status:", error);
    throw error;
  }
};

// Duplicate template (new template belongs to current user)
export const duplicateTemplate = async (id: string, userId: string): Promise<string> => {
  try {
    const original = await getAssessmentTemplateById(id, userId);
    if (!original) throw new Error("Template not found");

    // Get logged in user from localStorage
    const storedUser = JSON.parse(localStorage.getItem("currentUser") || "{}");

    const newTemplate: AssessmentTemplateForm = {
      name: `${original.name} (Copy)`,
      description: original.description,
      targetAudience: original.targetAudience,
      sections: original.sections,
      instructions: original.instructions,
      timeLimit: original.timeLimit,
      isActive: false,
    };

    return await createAssessmentTemplate(
      newTemplate,
      storedUser.id || storedUser.uid || userId,
      storedUser.organizationId || "",
      storedUser.organizationName || "",
      storedUser.role || ""
    );
  } catch (error) {
    console.error("Error duplicating template:", error);
    throw error;
  }
};

// Get active templates for current user only
export const getActiveTemplates = async (userId: string): Promise<AssessmentTemplate[]> => {
  try {
    if (!userId) {
      console.warn("No userId provided, returning empty array");
      return [];
    }

    const q = query(
      collection(db, TEMPLATES_COLLECTION),
      where("createdBy", "==", userId),
      where("isActive", "==", true),
      orderBy("createdAt", "desc")
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        name: data.name || "",
        description: data.description || "",
        targetAudience: data.targetAudience || "all",
        sections: data.sections || [],
        totalQuestions: data.totalQuestions || 0,
        totalDuration: data.totalDuration || 0,
        instructions: data.instructions || "",
        timeLimit: data.timeLimit || 0,
        isActive: data.isActive || false,
        createdBy: data.createdBy || "",
        createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        updatedAt: data.updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      } as AssessmentTemplate;
    });
  } catch (error) {
    console.error("Error fetching active templates:", error);
    throw error;
  }
};

// Get template count for current user
export const getTemplateCount = async (userId: string): Promise<number> => {
  try {
    if (!userId) {
      return 0;
    }

    const q = query(
      collection(db, TEMPLATES_COLLECTION),
      where("createdBy", "==", userId)
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.size;
  } catch (error) {
    console.error("Error getting template count:", error);
    throw error;
  }
};

// Get all templates (admin only - no user filter) - Use with caution
export const getAllTemplatesAdmin = async (): Promise<AssessmentTemplate[]> => {
  try {
    const q = query(collection(db, TEMPLATES_COLLECTION), orderBy("createdAt", "desc"));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        name: data.name || "",
        description: data.description || "",
        targetAudience: data.targetAudience || "all",
        sections: data.sections || [],
        totalQuestions: data.totalQuestions || 0,
        totalDuration: data.totalDuration || 0,
        instructions: data.instructions || "",
        timeLimit: data.timeLimit || 0,
        isActive: data.isActive || false,
        createdBy: data.createdBy || "",
        createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        updatedAt: data.updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      } as AssessmentTemplate;
    });
  } catch (error) {
    console.error("Error fetching all templates:", error);
    throw error;
  }
};